import { Box, CircularProgress } from "@mui/material";
import { useState, useRef, useCallback, useEffect } from "react";
import { Chat } from "../tctt.js";
import { theme } from "../theme.js";
import {
	getAssistantConversations,
	getAssistantConversation,
	createAssistantConversation,
	deleteAssistantConversation,
	renameAssistantConversation,
	streamAssistantChat,
} from "../api/index.js";

const AssistantScreen = () => {
	const chatRef = useRef(null);
	const abortControllerRef = useRef(null);
	const conversationIdMapRef = useRef({});
	const [isStreaming, setIsStreaming] = useState(false);
	const [initialConversations, setInitialConversations] = useState([]);
	const [loaded, setLoaded] = useState(false);

	useEffect(() => {
		const load = async () => {
			try {
				const conversations = await getAssistantConversations().catch(() => []);
				if (Array.isArray(conversations)) {
					setInitialConversations(
						conversations.map((conv) => ({
							id: conv._id,
							title: conv.title || "New Conversation",
							lastMessage: conv.lastMessage || "",
							createdAt: conv.createdAt,
							lastTimestamp: conv.lastTimestamp || conv.createdAt,
						}))
					);
				}
			} catch (error) {
				console.error("Failed to load conversations:", error);
			} finally {
				setLoaded(true);
			}
		};

		load();
		return () => {
			if (abortControllerRef.current) abortControllerRef.current.abort();
		};
	}, []);

	const resolveConversationId = useCallback((frontendId) => {
		return conversationIdMapRef.current[frontendId] || frontendId;
	}, []);

	const handleConversationSelected = useCallback(
		async (conversationId) => {
			if (!conversationId) return;
			try {
				const existingMessages = chatRef.current?.getMessages(conversationId) || [];
				if (existingMessages.length > 0) return;

				const dbId = resolveConversationId(conversationId);
				const conversation = await getAssistantConversation(dbId);

				if (conversation?.messages && Array.isArray(conversation.messages) && chatRef.current?.setMessages) {
					chatRef.current.setMessages(
						conversationId,
						conversation.messages.map((msg) => ({
							role: msg.role,
							content: msg.content,
							timestamp: new Date(msg.timestamp || msg.createdAt).toLocaleTimeString([], {
								hour: "2-digit",
								minute: "2-digit",
							}),
						}))
					);
				}
			} catch (error) {
				console.error("Failed to load messages:", error);
			}
		},
		[resolveConversationId]
	);

	const parseSSEStream = useCallback(async (response, conversationId) => {
		const reader = response.body.getReader();
		const decoder = new TextDecoder();
		let accumulatedContent = "";
		let assistantMessageAdded = false;

		// Convert ReadableStream into an async iterable
		async function* streamChunks(reader) {
			let buffer = "";
			try {
				let done = false;
				let value;
				// Read until stream ends
				({ done, value } = await reader.read());
				while (!done) {
					buffer += decoder.decode(value, { stream: true });
					const lines = buffer.split("\n");
					buffer = lines.pop() || "";

					for (const line of lines) {
						const trimmed = line.trim();
						if (!trimmed || !trimmed.startsWith("data:")) continue;
						yield trimmed.slice(5).trim();
					}

					({ done, value } = await reader.read());
				}
			} finally {
				reader.releaseLock();
			}
		}

		for await (const data of streamChunks(reader)) {
			if (data === "[DONE]") return;

			try {
				const parsed = JSON.parse(data);
				if (parsed.type === "content" && parsed.content) {
					accumulatedContent += parsed.content;
					if (!assistantMessageAdded) {
						chatRef.current?.addAssistantMessage(accumulatedContent, conversationId);
						assistantMessageAdded = true;
					} else {
						chatRef.current?.updateLastAssistantMessage(accumulatedContent, conversationId);
					}
				} else if (parsed.type === "done") {
					return;
				} else if (parsed.type === "error") {
					const errorMsg = parsed.error || "Something went wrong.";
					if (!assistantMessageAdded) {
						chatRef.current?.addAssistantMessage(`⚠️ ${errorMsg}`, conversationId);
					} else {
						chatRef.current?.updateLastAssistantMessage(
							accumulatedContent + `\n\n⚠️ ${errorMsg}`, conversationId
						);
					}
					return;
				}
			} catch {
				// skip
			}
		}
	}, []);

	const handleSendMessage = useCallback(
		async (message, conversationId) => {
			if (abortControllerRef.current) abortControllerRef.current.abort();

			const abortController = new AbortController();
			abortControllerRef.current = abortController;
			setIsStreaming(true);

			const dbConversationId = resolveConversationId(conversationId);
			const existingMessages = chatRef.current?.getMessages(conversationId) || [];
			const lastExisting = existingMessages[existingMessages.length - 1];
			const alreadyIncluded = lastExisting && lastExisting.role === "user" && lastExisting.content === message.content;

			const messagesPayload = existingMessages.map((msg) => ({ role: msg.role, content: msg.content }));
			if (!alreadyIncluded && message.content) messagesPayload.push({ role: "user", content: message.content });

			const filtered = messagesPayload.filter((msg) => msg.content && msg.content.trim().length > 0);

			try {
				const response = await streamAssistantChat(dbConversationId, filtered);
				if (!response.ok) {
					let errorMessage = `Error: ${response.statusText}`;
					try {
						const errorBody = await response.text();
						const errorJson = JSON.parse(errorBody);
						errorMessage = errorJson.error || errorMessage;
					} catch { /* not JSON */ }
					throw new Error(errorMessage);
				}

				abortController.signal.addEventListener("abort", () => { response.body?.cancel(); });
				await parseSSEStream(response, conversationId);
			} catch (error) {
				if (error.name === "AbortError") return;
				console.error("Streaming error:", error);
				chatRef.current?.addAssistantMessage("Sorry, something went wrong. Please try again.", conversationId);
			} finally {
				setIsStreaming(false);
				abortControllerRef.current = null;
			}
		},
		[parseSSEStream, resolveConversationId]
	);

	const handleStopGenerating = useCallback(() => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
			setIsStreaming(false);
		}
	}, []);

	const handleConversationCreated = useCallback(async (conversation) => {
		try {
			const result = await createAssistantConversation(conversation.title);
			if (result?._id) conversationIdMapRef.current[conversation.id] = result._id;
		} catch (error) {
			console.error("Failed to create conversation:", error);
		}
	}, []);

	const handleConversationDeleted = useCallback(
		async (conversationId) => {
			try {
				const dbId = resolveConversationId(conversationId);
				await deleteAssistantConversation(dbId);
				delete conversationIdMapRef.current[conversationId];
			} catch (error) {
				console.error("Failed to delete conversation:", error);
			}
		},
		[resolveConversationId]
	);

	const handleConversationRenamed = useCallback(
		async (conversationId, newTitle) => {
			try {
				const dbId = resolveConversationId(conversationId);
				await renameAssistantConversation(dbId, newTitle);
			} catch (error) {
				console.error("Failed to rename conversation:", error);
			}
		},
		[resolveConversationId]
	);

	if (!loaded) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "calc(100vh - 130px)" }}>
				<CircularProgress size={28} sx={{ color: theme.palette.primary.main }} />
			</Box>
		);
	}

	return (
		<Box sx={{ height: "100%", p: 2 }}>
			<Chat
				ref={chatRef}
				onSendMessage={handleSendMessage}
				onStopGenerating={handleStopGenerating}
				onConversationCreated={handleConversationCreated}
				onConversationDeleted={handleConversationDeleted}
				onConversationRenamed={handleConversationRenamed}
				onConversationSelected={handleConversationSelected}
				isStreaming={isStreaming}
				primaryColor={theme.palette.primaryLight.main}
				secondaryColor={theme.palette.third.main}
				backgroundColor={theme.palette.primary.main}
				userMessageColor="#1A2636"
				assistantMessageColor="#1A2636"
				placeholder="Ask me anything..."
				showSidebarHeader
				sidebarTitle="Conversations"
				showChatHeader
				chatTitle="AI Assistant"
				initialConversations={initialConversations}
			/>
		</Box>
	);
};

export default AssistantScreen;
